import React, { useState, useEffect } from "react";
import "../../../App.css";
import { createModel, updateModel, getAllKGColumns, saveKGColumn, getMetadataDependency, updateKGColumn } from "../../../services/DataModelService";
import AddDescription from "../../../components/configuration/snowflake/AddDescription";
import { ColumnInfo, FormatProto, Header, ModelInfo, ObservationColumnProto } from "build-ts/metadata";
import { DataType, ColumnType, AggregationType, dataTypeFromJSON, columnTypeFromJSON, aggregationTypeFromJSON, TimeBucket, PolarityEnum, MetadataTypeEnum, metadataTypeEnumToJSON } from "build-ts/enums";
import showToast from "hooks/useCustomToast";
import { KGColumnProto } from "build-ts/knowledege_graph";
import { CreateOrUpdateMetrics_Request, DependencyProto } from "build-ts/interface";
import { ObservationContentProto } from "build-ts/metadata";
import { createMetric } from "services/MetricService";

/**
 *
 * Input: modelInfo object containing information about the data model.
 *        navigateTo(set to true) callback to navigate to another component from parent component.
 * Return: Renders the AddDescription component with relevant props.
 *
 * Handles user input for updating model information, including header, columns, and descriptions.
 * Submits the updated model information to the API.
 *
 * @component
 */

const AddDescriptionContainer: React.FC<{
  modelInfo: ModelInfo | null;
  navigateTo: ((data: boolean) => void) | null; 
  updateFlag: boolean;
  handleCreateDataModel: (() => void) | null;
  onBack: () => void;
}> = ({ modelInfo, navigateTo, updateFlag, handleCreateDataModel, onBack }) => {
  // State to store the header of Data Model
  const [header, setHeader] = useState<Header>(modelInfo?.header! || {});
  const [buttonLoader, setButtonLoader] = useState<boolean>(false);
  
  // State to store the columns of Data Model
  const [columns, setColumns] = useState<ColumnInfo[]>(modelInfo?.columns || []);
  const [kgColumns, setKgColumns] = useState<Header[]>([]);
  const [shouldReinitialize, setShouldReinitialize] = useState<boolean>(false);
  // Request list for Creating a new Metrics
  const [metricRequestList, setMetricRequestList] = useState<CreateOrUpdateMetrics_Request[]>([]);
  // List of Metrics which are selected
  const [selectedMetrics, setSelectedMetrics] = useState<{ [columnIndex: number]: Header }>({});
  // Map of kg-columns dependent on logical columns
  const [kgColumnsDependentOnLogicalColumns, setKgColumnsDependentOnLogicalColumns] = useState<Map<string, Header[]>>(new Map());
  // Map of metrics dependent on logical columns
  const [metricsDependentOnLogicalColumns, setMetricsDependentOnLogicalColumns] = useState<Map<string, Header[]>>(new Map());
  // Adding and Removing Mapping requests
  const [mappingRequests, setMappingRequests] = useState<{ [columnIndex: number]: {kgColumn: Header, isAdding: boolean}[] }>({});
  // Track initial KG column mappings for comparison (to detect changes)
  const [initialKgColumnMappings, setInitialKgColumnMappings] = useState<{ [columnIndex: number]: Header | null }>({});
  

  // Function to check if the model info has changed
  const hasModelInfoChanged = () : boolean => {
    const originalModelInfo = modelInfo;
    if(!originalModelInfo?.columns){
      return false;
    }
    const currentColumns = columns;
    const originalColumns = originalModelInfo?.columns;
    if(currentColumns?.length !== originalColumns?.length){
      return true;
    }
    for (let i = 0; i < originalColumns?.length; i++){
      const currentColumn = currentColumns[i];

      if(
        currentColumn.header?.description !== originalColumns[i]?.header?.description ||
        currentColumn.column_type !== originalModelInfo?.columns[i]?.column_type ||
        currentColumn.data_type !== originalModelInfo?.columns[i]?.data_type ||
        currentColumn.default_aggregation !== originalModelInfo?.columns[i]?.default_aggregation
      ){
        return true;
      }
    }
    return false;
  };

  // Fetch KG columns function - defined here so it can be passed to children
  const refreshKGColumns = async () => {
    const data = await getAllKGColumns();
    if (data) {
      setKgColumns(data);
    }
  };

  // Fetch kg-columns and metrics dependent on logical columns
  const fetchDepenedentKgColumnsAndMetrics = async (columnIds: string[]) => {
    const data = await getMetadataDependency(
      columnIds,
      metadataTypeEnumToJSON(MetadataTypeEnum.LOGICAL_COLUMN),
      [metadataTypeEnumToJSON(MetadataTypeEnum.KG_COLUMN), metadataTypeEnumToJSON(MetadataTypeEnum.METRIC)],
      true
    );
    const kgColumnsDependencies: Map<string, Header[]> = new Map();
    const metricsDependencies: Map<string, Header[]> = new Map();
    if(data){
      data.forEach((dependency: DependencyProto) => {
        if(dependency.headers.length > 0 && dependency.metadata_id){
          const kgHeaders: Header[] = [];
          const metricHeaders: Header[] = [];
          
          dependency.headers.forEach(header => {
            if(header.type === MetadataTypeEnum.KG_COLUMN){
              kgHeaders.push(header);
            }
            else if(header.type === MetadataTypeEnum.METRIC){
              metricHeaders.push(header);
            }
          });
          
          if(kgHeaders.length > 0){
            kgColumnsDependencies.set(dependency.metadata_id, kgHeaders);
          }
          if(metricHeaders.length > 0){
            metricsDependencies.set(dependency.metadata_id, metricHeaders);
          }
        }
      });
    }
    setKgColumnsDependentOnLogicalColumns(kgColumnsDependencies);
    setMetricsDependentOnLogicalColumns(metricsDependencies);
    
    // Initialize the initial mappings for comparison
    const initialMappings: { [columnIndex: number]: Header | null } = {};
    columns.forEach((column, index) => {
      const mappedKgColumn = kgColumnsDependencies.get(column.header?.id || "");
      initialMappings[index] = mappedKgColumn?.[0] || null;
    });
    setInitialKgColumnMappings(initialMappings);
    
    // Pre-select existing dependent metrics for UI
    const preSelectedMetrics: { [columnIndex: number]: Header } = {};
    columns.forEach((column, index) => {
      const dependentMetric = metricsDependencies.get(column.header?.id || "");
      if (dependentMetric && dependentMetric.length > 0) {
        preSelectedMetrics[index] = dependentMetric[0];
      }
    });
    setSelectedMetrics(preSelectedMetrics);
  };

  // Function to initialize columns data
  const initializeColumnsData = async () => {
    setShouldReinitialize(true);
  };

  // Function to initialize matching columns data
  const initializeMatchingColumnsData = async () => {
    // This will be handled by the same useEffect as initializeColumnsData
    setShouldReinitialize(true);
  };

  useEffect(() => {
    // Fetch KG columns and its dependent metrics and kg-columns when component mounts
    const initializeColumns = async () => {
      await refreshKGColumns();
    };
    if(columns.length > 0 && updateFlag){
      fetchDepenedentKgColumnsAndMetrics(columns.map(column => column.header?.id || ""));
    }
    initializeColumns();
  }, []);

  // Re-initialize when shouldReinitialize flag is set
  useEffect(() => {
    if (shouldReinitialize) {
      const reinitialize = async () => {
        await refreshKGColumns();
        setShouldReinitialize(false);
      };
      reinitialize();
    }
  }, [shouldReinitialize, columns]); // Depend on columns so it uses updated state

  /**
   * Handles changes in the header properties.
   *
   * @param {string} property - The property to be updated.
   * @param {string} value - The new value for the property.
   */
  const handleHeaderChange = (property: string, value: string) => {
    setHeader({
      ...header,
      [property]: value,
    });
  };

  /**
   * Handles changes in the column properties.
   *
   * @param {number} index - The index of the column to be updated.
   * @param {"data_type" | "column_type" | "default_aggregation"} property - The property to be updated.
   * @param {string} value - The new value for the property.
   */
  const handleColumnChange = (
    index: number,
    property: "data_type" | "column_type" | "default_aggregation",
    value: string
  ) => {
    const newColumns = [...columns];
    let parsedValue: DataType | ColumnType | AggregationType | undefined;

    switch(property) {
      case "data_type":
        parsedValue = dataTypeFromJSON(value);
        break;
      case "column_type":
        parsedValue = columnTypeFromJSON(value);
        break;
      case "default_aggregation":
        parsedValue = aggregationTypeFromJSON(value);
        break;
      default:
        parsedValue = undefined;
    }

    newColumns[index] = {
      ...newColumns[index],
      [property]: parsedValue,
    };

    // Dependencies change due to data_type
    if(property === "data_type") {
      let columnType: ColumnType | undefined;
      if([DataType.VARCHAR, DataType.BOOLEAN].includes(parsedValue as DataType)){
        columnType = ColumnType.ATTRIBUTE;
        delete newColumns[index].default_aggregation;
      }
      else if ([DataType.INT32, DataType.INT64, DataType.DOUBLE].includes(parsedValue as DataType)) {
        columnType = ColumnType.MEASURE;
        if (!newColumns[index].default_aggregation){
          newColumns[index].default_aggregation = AggregationType.SUM;
        }
      } 
      else if ([DataType.DATE, DataType.DATETIME].includes(parsedValue as DataType)) {
        columnType = ColumnType.TEMPORAL;
        delete newColumns[index].default_aggregation;
      }
      newColumns[index].column_type = columnType;
    }

    // Dependencies change due to column_type
    if(property === "column_type") {
      if([ColumnType.ATTRIBUTE, ColumnType.TEMPORAL].includes(parsedValue as ColumnType)){
        delete newColumns[index].default_aggregation;
      }
      else{
        newColumns[index].default_aggregation = AggregationType.SUM;
      }
    }

    setColumns(newColumns);
  };

  /**
   * Handles changes in the column header properties.
   *
   * @param {number} index - The index of the column to be updated.
   * @param {string} property - The property to be updated.
   * @param {string} value - The new value for the property.
   */
  const handleColumnHeaderChange = (
    index: number,
    property: string,
    value: string,
  ) => {
    const newColumns = [...columns];

    newColumns[index] = {
      ...newColumns[index],
      header: {
        ...newColumns[index].header,
        [property]: value,
      },
    };

    setColumns(newColumns);
  };

  /**
   * Handles changes in the KG column header.
   *
   * @param {number} index - The index of the column to be updated.
   * @param {string} kgColumnId - The ID of the selected KG column.
   */
  const handleKGColumnChange = (
    index: number,
    kgColumnId: string,
    newHeader: Header | null
  ) => {
    let selectedKGColumn = kgColumns.find(col => col.id === kgColumnId);

    if (selectedKGColumn === null && newHeader !== null) {
      selectedKGColumn = newHeader;
    }

    // If kgColumnId is empty or "None" was selected, selectedKGColumn will be undefined
    if (!kgColumnId) {
      selectedKGColumn = undefined;
    }

    // Get the current/previous mapping
    const currentMapping = initialKgColumnMappings[index];
    const newMapping = selectedKGColumn || null;

    // Update mapping requests state
    setMappingRequests(prev => {
      const newRequests = { ...prev };
      
      // Check if there's actually a change
      if (currentMapping?.id !== newMapping?.id) {
        if (newMapping) {
          // Case 1: Assigning a new KG column (first time or changing)
          // Initialize array if it doesn't exist
          if (!newRequests[index]) {
            newRequests[index] = [];
          }
          
          // If there's a previous mapping, remove it first
          if (currentMapping) {
            newRequests[index].push({
              kgColumn: currentMapping,
              isAdding: false
            });
          }
          
          // Then add the new KG column mapping
          newRequests[index].push({
            kgColumn: newMapping,
            isAdding: true
          });
        } else {
          // Case 2: Selecting "None" - remove the previous mapping
          if (currentMapping) {
            newRequests[index] = [{
              kgColumn: currentMapping,
              isAdding: false
            }];
          } else {
            // No previous mapping and selecting "None" - no action needed
            delete newRequests[index];
          }
        }
      } else {
        // No change from initial state, remove any pending requests
        delete newRequests[index];
      }
      
      return newRequests;
    });

    // Update dependentKgColumns to reflect the UI change immediately
    setKgColumnsDependentOnLogicalColumns(prev => {
      const newDependentKgColumns = new Map(prev);
      const logicalColumnId = columns[index].header?.id;
      
      if (logicalColumnId) {
        if (newMapping) {
          // Set the new KG column mapping
          newDependentKgColumns.set(logicalColumnId, [newMapping]);
        } else {
          // Remove the mapping (selecting "None")
          newDependentKgColumns.delete(logicalColumnId);
        }
      }
      
      return newDependentKgColumns;
    });
  };
  

  // Handle metric selection for a column
  const handleMetricSelection = (columnIndex: number, selectedMetric: Header | null) => {
    if (selectedMetric) {
      setSelectedMetrics(prev => ({ ...prev, [columnIndex]: selectedMetric }));
    } else {
      setSelectedMetrics(prev => {
        const newState = { ...prev };
        delete newState[columnIndex];
        return newState;
      });
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      const model_info: ModelInfo = {
          header: header, // Updated header with name and description
          data_source: modelInfo?.data_source, // Unchanged data source info
          columns: columns
      };

      setButtonLoader(true);
      if(!updateFlag){
        const data = await createModel(model_info);
        if(data){
          const modelId = data?.header?.id;  // get the model id from the response for the first time creation of the model
          await handleCreateMetrics(modelId); // create new-metrics for the model.

          // Add Mapping requests for the logical columns and kg-columns.
          await handleMappingRequest();
          
          showToast("Model created successfully! Redirecting to Graph", "", "success");
          navigateTo && navigateTo(true); // Navigate to the Graph

        } 
      }
      else{
        if(metricRequestList.length > 0){
          setMetricRequestList([]);
        }

        // check if the model info has changed
        const hasModelChanged = hasModelInfoChanged();
        if(hasModelChanged){
          await updateModel(model_info);
        }
        if(Object.keys(mappingRequests).length > 0){
          // Handle mapping requests for the updated model as well
          await handleMappingRequest();
        }
        showToast("Model updated successfully! Redirecting to Graph", "", "success");
        navigateTo && navigateTo(true); // Navigate to the Graph
      }
      setButtonLoader(false);
    } catch (error) {
      // Handle errors that may occur during the API call
      showToast("Error sending data to API create/update model", "", "error");
      setButtonLoader(false); // Ensure loader is turned off in case of error
    }
  };

  // Function to create a new KG Column
  const handleCreateKGColumn = async (name: string, columnType: ColumnType, index: number): Promise<boolean> => {
    try {
      const kgColumnProto = KGColumnProto.create({
        header: Header.create({
          name: name,
          description: ""
        }),
        column_type: columnType,
      });
      
      // Add properties based on column type
      if (columnType === ColumnType.ATTRIBUTE) {
        kgColumnProto.cardinality = undefined;
      } else if (columnType === ColumnType.MEASURE) {
        kgColumnProto.aggregation = AggregationType.SUM;
      } else if (columnType === ColumnType.TEMPORAL) {
        kgColumnProto.time_bucket = TimeBucket.DAILY;
      }

      const result = await saveKGColumn(kgColumnProto, false);
      if(result){
        //await refreshKGColumns();
        setKgColumns(prev => [...prev, result.header!]);
        showToast(`${result.header?.name} KG Column created successfully`, "", "success");
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error creating KG Column:", error);
      showToast("Error creating KG Column", "", "error");
      return false;
    }
  };

  const handleAddMetricCreationRequest = (columnId: string, columnName: string) => {
    const metricRequest = CreateOrUpdateMetrics_Request.create({
      model_id: modelInfo?.header?.owner_id || "",
      metrics: [
        {
          header: Header.create({ name: columnName, description: ""}),
          definition: ObservationContentProto.create({
            observation_columns: [ObservationColumnProto.create({
              logical_column_header: Header.create({ id: columnId }),
              aggregation: AggregationType.SUM,
            })]
          }),
          format: FormatProto.create({
            currency_code: "USD",
            num_decimals: 0,
          }),
          polarity: PolarityEnum.POSITIVE,
        }
      ],
      update_edges: false,
    });
  
    setMetricRequestList(prev => [...prev, metricRequest]);
  };

  // Handle Create Metrics
  const handleCreateMetrics = async (modelId?: string) => {
    // If there are no metrics to create, then return without creating any metrics
    if(metricRequestList.length > 0){
      for (const metricRequest of metricRequestList) {
        const request = CreateOrUpdateMetrics_Request.create({
          model_id: modelId,
          metrics: metricRequest.metrics,
          update_edges: false,
        });
        const response = await createMetric(request);
        if(response){
          showToast(`${response.header?.name} Metric created successfully`, "", "success");
        }
      }
    } else {
      // then return without creating any metrics
      return;
    }
  };

  // Handle Add Mapping or Remove Mapping request
  const handleMappingRequest = async () => {
    try {
      // Process each mapping request
      for (const [columnIndexStr, requestArray] of Object.entries(mappingRequests)) {
        const columnIndex = parseInt(columnIndexStr);
        const logicalColumn = columns[columnIndex];
        const logicalColumnId = logicalColumn.header?.id;
        
        if (!logicalColumnId) {
          continue;
        }

        // Process each request in the array for this column
        for (const request of requestArray) {
          // Create KGColumnProto
          const kgColumnProto = KGColumnProto.create({
            header: request.kgColumn
          });

          if (request.isAdding) {
            // Add mapping
            await updateKGColumn(
              null,
              kgColumnProto.header?.id, // kgColumnId
              [logicalColumnId], // addMappings
              [], // removeMappings
              false // updateLogicalMappings
            );
          } else {
            // Remove mapping
            await updateKGColumn(
              null,
              kgColumnProto.header?.id, // kgColumnId
              [], // addMappings
              [logicalColumnId], // removeMappings
              false // updateLogicalMappings
            );
          }
        }
      }
      
      // Clear mapping requests after processing
      setMappingRequests({});
    } catch (error) {
      console.error("Error processing mapping requests:", error);
      showToast("Error updating KG column mappings", "", "error");
    }
  };

  return (
    // Render the AddDescription component with relevant props
    <AddDescription
      header={header}
      columns={columns}
      dependentKgColumns={kgColumnsDependentOnLogicalColumns}
      dependentMetrics={metricsDependentOnLogicalColumns}
      handleHeaderChange={handleHeaderChange}
      handleColumnChange={handleColumnChange}
      handleColumnHeaderChange={handleColumnHeaderChange}
      handleKGColumnChange={handleKGColumnChange}
      handleSubmit={handleSubmit}
      updateFlag={updateFlag}
      buttonLoader={buttonLoader}
      kgColumns={kgColumns}
      modelId={modelInfo?.header?.id}
      refreshKGColumns={refreshKGColumns}
      initializeColumns={initializeColumnsData}
      initializeMatchingColumns={initializeMatchingColumnsData}
      handleCreateKGColumn={handleCreateKGColumn}
      handleAddMetricCreationRequest={handleAddMetricCreationRequest}
      handleMetricChange={handleMetricSelection}
      selectedMetrics={selectedMetrics}
      metricRequestList={metricRequestList}
      onBack={onBack}
    />
  );
};

export default AddDescriptionContainer;
